import { useLoaderData, useNavigation, useNavigate } from '@remix-run/react';
import { useState, useEffect, useRef } from 'react';
import { atom } from 'nanostores';
import type { UIMessage } from 'ai';
import { toast } from 'react-toastify';
import { workbenchStore } from '~/lib/stores/workbench';
import {
  getFileActions,
  getMessages,
  openDatabase,
  runProjectIdMigration,
  setFileActions,
  setMessages,
  setCloudChatId,
  upsertEvent,
  pushEventToQueueIfComplete,
  processFileActions,
  withContentHash,
  setProjectType,
  updateNetlifyUrl,
  getFailedEventsFromDB,
  deleteEvents,
} from './db';
import type { StoreFileAction } from '~/types/actions';
import { sanitizePostgresString } from '~/utils/getTemplates';
import {
  githubBranchStore,
  githubOwnerStore,
  githubRepoStore,
  supabaseProjectIdStore,
  supabaseProjectNameStore,
  supabaseAnonKeyStore,
  supabaseOrganizationIdStore,
  supabaseOrganizationNameStore,
} from '~/lib/stores/integrations';
import { type ConnectionData, type Connections, type CloudChatHistoryItem } from '~/services/interfaceService';
import { setEventStatus, type EventErrorDetails } from '~/lib/stores/eventStatus';
import {
  assignCreatedAtTimestamps,
  updateLastOpenedOn,
  updateProjectConnection,
  updateProjectType,
} from '~/utils/helper';
import * as Sentry from '@sentry/remix';
import type { ChatsMetadata } from '~/services/dbSchema';
import { v4 as uuidv4 } from 'uuid';
import type { ChatServerLoaderData } from '~/routes/chat.$projectId';
import { chatStore } from '~/lib/stores/chat';
import { techStackSelectionStore } from '~/lib/stores/techStackSelection';
import { addProjectToStore } from '~/utils/helper';
import { ensureEventStatusConnection } from '~/lib/utils/eventStatusConnection';
import { setStatus } from '~/lib/stores/chatState';

const persistenceEnabled = !import.meta.env.VITE_DISABLE_PERSISTENCE;

console.log('[projectId] openDatabase called from useChatHistory');

export const db = persistenceEnabled ? await openDatabase() : undefined;

export const description = atom<string | undefined>(undefined);
export const initSourceStore = atom<string>('alpha');

/*
 * export const chatId = atom<string | undefined>(undefined); // equivalent to projectId in the new schema
 * export const urlIdStore = atom<string | undefined>(undefined);
 */

export const projectId = atom<string | undefined>(undefined);
export const cloudChatId = atom<string | undefined>(undefined);
export const versionId = atom<string | undefined>(undefined);

// Lock to prevent concurrent project creation
let projectCreationPromise: Promise<void> | null = null;

const getData = async () => {
  const { firstAIArtifact } = workbenchStore;

  if (!description.get()) {
    const repoName = githubRepoStore.get();
    const ownerName = githubOwnerStore.get();
    const branch = githubBranchStore.get();
    const isGitRepo = !!(repoName && ownerName && branch);

    if (isGitRepo) {
      description.set(repoName);
    } else if (firstAIArtifact?.title) {
      description.set(firstAIArtifact.title);
    }
  }

  console.log('[flow] getData projectId: ', projectId.get());

  if (!projectId.get()) {
    // If a project is already being created, wait for it
    if (projectCreationPromise) {
      console.log('[flow] Project creation already in progress, waiting...');
      await projectCreationPromise;
      return;
    }

    // Create a new promise and store it
    projectCreationPromise = (async () => {
      try {
        console.log('[flow] Starting project creation...');

        const { framework, language, packageManager } = techStackSelectionStore.get();
        const repoName = githubRepoStore.get();
        const ownerName = githubOwnerStore.get();
        const branch = githubBranchStore.get();
        const isGitRepo = !!(repoName && ownerName && branch);

        console.log('[getData] description from !projectId: ', description.get());

        await createCloudProject(
          framework,
          language,
          packageManager,
          initSourceStore.get(),
          isGitRepo,
          description.get() || '',
        );
        console.log('[getData] Project creation completed successfully');
      } finally {
        // Clear the promise once done
        projectCreationPromise = null;
      }
    })();

    await projectCreationPromise;
  }
};

export function useChatHistory() {
  const navigation = useNavigation();
  const navigate = useNavigate();

  const loaderData = useLoaderData<ChatServerLoaderData>();
  const { token, projectData, hasCache: cacheHit, isCacheValid, eventsToDelete } = loaderData;

  const {
    projectId: mixedProjectId,
    cloudChatId: mixedCloudChatId,
    currentVersion,
    projectName,
    messages: loaderMessages,
    fileActions: loaderFileActions,
  } = projectData ?? {};

  console.log(
    '[useChatHistory] data from loader, cachehit, isCacheValid, projectData and eventsToDelete:',
    cacheHit,
    isCacheValid,
    projectData,
    eventsToDelete,
  );

  const [initialMessages, setInitialMessages] = useState<UIMessage[]>([]);
  const [initialFileActions, setInitialFileActions] = useState<StoreFileAction[]>([]);
  const [ready, setReady] = useState<boolean>(false);
  const [showCloudProjectErrorModal, setShowCloudProjectErrorModal] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>('');

  useEffect(() => {
    console.log('[projectId] location of useChatHistory: ', window.location.href);

    if (db && token && !window.location.href.includes('chat/')) {
      console.log('[projectId] Token available in useChatHistory, triggering deferred migration if needed');
      runProjectIdMigration(db, token).then(() => {
        /*
         * Note: Not refreshing sidebar here as migration typically doesn't create new projects
         * If needed in the future, consider checking migration result and conditionally refreshing
         */
      });
    }
  }, [token]);

  useEffect(() => {
    // Wait for navigation to complete
    if (navigation.state === 'loading') {
      console.log('[useChatHistory] Navigation still loading, waiting...');
      return;
    }

    if (!db) {
      setReady(true);

      if (persistenceEnabled) {
        toast.error(`Chat persistence is unavailable`);
      }

      return;
    }

    /*
     * console.log(
     *   '[useChatHistory] mixedId:',
     *   mixedId,
     *   'mixedProjectId:',
     *   mixedProjectId,
     *   'mixedCloudChatId:',
     *   mixedCloudChatId,
     *   'currentVersion:',
     *   currentVersion,
     * );
     */

    if (!mixedProjectId) {
      setReady(true);
      return;
    }

    const loadChatData = async () => {
      try {
        console.log('[useChatHistory] loading chat data for existing project');

        ensureEventStatusConnection(mixedProjectId as string, mixedCloudChatId, token!);

        // update the project's lastOpenedOn timestamp in db
        const lastOpenedOn = new Date().toISOString();
        updateLastOpenedOn(mixedProjectId, lastOpenedOn);

        /*
         * Note: Not refreshing sidebar here - this runs every time a chat loads
         * and would cause unnecessary API calls just for a timestamp update
         */

        // Load and restore failed events from IndexedDB for current project only
        if (mixedProjectId) {
          try {
            const failedEvents = await getFailedEventsFromDB(db, mixedProjectId);

            if (failedEvents.length > 0) {
              console.log(
                '[useChatHistory] Restoring failed events from IndexedDB for project:',
                mixedProjectId,
                failedEvents.length,
              );

              // Restore each failed event to the event status store
              for (const event of failedEvents) {
                const error: EventErrorDetails = event.error
                  ? event.error
                  : {
                      message: 'ENQUEUE_ERROR',
                      code: 'ENQUEUE_ERROR',
                      retryable: true,
                      errorType: 'ENQUEUE_ERROR',
                      metadata: {
                        eventId: event.eventId,
                        projectId: event.projectId,
                        cloudChatId: event.cloudChatId,
                      },
                    };

                if (error) {
                  setEventStatus(event.eventId, {
                    status: 'FAILED',
                    eventState: error.errorType,
                    projectId: event.projectId,
                    chatId: event.cloudChatId,
                    userId: event.userId,
                    timestamp: new Date().toISOString(),
                    error,
                  });
                  console.log('[useChatHistory] Restored failed event:', event.eventId);
                }
              }
            }
          } catch (error) {
            console.warn('[useChatHistory] Failed to load failed events from IndexedDB:', error);
          }
        }

        // loading data from IDB
        if (cacheHit && isCacheValid) {
          console.log('[useChatHistory] Loading from IDB (server skipped query)');

          try {
            const cached = await getMessages(db, mixedProjectId);

            console.log('[useChatHistory] cached:', cached);

            console.log('[useChatHistoryy] cached type:', cached?.type);

            if (cached && cached.messages && cached.messages.length > 0) {
              // Sanitize any null bytes or problematic characters from cached messages content
              cached.messages = cached.messages.map((msg) => ({
                ...msg,
                content: typeof msg.content === 'string' ? sanitizePostgresString(msg.content) : msg.content,
              }));

              cloudChatId.set(cached.cloudChatId);
              description.set(cached.description);
              projectId.set(cached.projectId);

              setInitialMessages(cached.messages);
              setInitialFileActions(cached.fileActions);

              // upload older local project to cloud
              if (cached?.type == undefined || cached?.type == 'local') {
                console.log('[useChatHistory] uploading older local project to cloud');

                // status is set to importing so that older larger projects go in the github import flow and multiple events don't get enqueued
                setStatus('importing');

                if (!cached.projectId) {
                  throw new Error('Project ID is required');
                }

                const lastAssistantMessageId = cached.messages.findLast((msg) => msg.role === 'assistant')?.id || '';
                chatStore.setKey('lastAssistantMessageId', lastAssistantMessageId || '');

                // create chat metadata and get cloudChatId for older local projects
                const cId = uuidv4();
                const chatMetaData: Partial<ChatsMetadata> = {
                  chatId: cId,
                  projectId: cached.projectId,
                  chatName: 'Untitled test cloud chat',
                };

                try {
                  console.log('[useChatHistory] creating chat metadata for local project');

                  const response = await fetch('/api/chatMetaData', {
                    method: 'POST',
                    headers: {
                      'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({ chatMetaData }),
                  });

                  const result = (await response.json()) as any;

                  // console.log('[useChatHistoryy] result from chatMetaData:', result);

                  if (result.ok) {
                    // Set the cloudChatId in the store
                    cloudChatId.set(cId);

                    // Update cloudChatId in IDB
                    updateCloudChatIdInIDB(cId);

                    assignCreatedAtTimestamps(cached.messages);

                    // Process file actions to compute contentHash
                    const processedFileActions = processFileActions(cached.fileActions);
                    console.log('[useChatHistory] processedFileActions >>>', processedFileActions);

                    const fileActionsWithHash: StoreFileAction[] = await Promise.all(
                      processedFileActions.map(withContentHash),
                    );

                    // Update file actions in IDB with contentHash
                    await setFileActions(db, cached.projectId, cId, fileActionsWithHash, false);
                    await setProjectType(db, cached.projectId, 'private');
                    updateProjectType(cached.projectId, 'private');

                    setInitialMessages(cached.messages);
                    setInitialFileActions(fileActionsWithHash);

                    console.log('[useChatHistory] upserting event with fileActionsWithHash >>>', fileActionsWithHash);

                    // upsert the event with the proper cloudChatId and fileActions with contentHash
                    upsertEvent(db, {
                      eventId: lastAssistantMessageId,
                      projectId: cached.projectId,
                      cloudChatId: cId,
                      userId: token || 'anonymous',
                      eventName: 'LOCAL_PROJECT_UPLOAD',
                      timestamp: cached.timestamp,
                      messages: cached.messages,
                      startingIndex: 0,
                      messagesSet: true,
                      fileActions: fileActionsWithHash,
                      fileActionsSet: true,
                    });
                    console.log('[enqueueing event] pushEventToQueueIfComplete called from createEventForLocalProject');
                    pushEventToQueueIfComplete(db, chatStore.get().lastAssistantMessageId);
                  } else {
                    Sentry.captureException(
                      new Error('Failed to create chat metadata while uploading older local project to cloud'),
                      {
                        extra: {
                          projectId: cached.projectId,
                          error: result.error,
                        },
                      },
                    );

                    console.error('[useChatHistory] Failed to create chat metadata:', result.error);

                    toast.error('Failed to upload data to cloud');
                  }
                } catch (error) {
                  Sentry.captureException(
                    new Error('Failed to create chat metadata while uploading older local project to cloud'),
                    {
                      extra: {
                        projectId: cached.projectId,
                        error,
                      },
                    },
                  );

                  console.error('[useChatHistory] Error creating chat metadata:', error);
                  toast.error('Failed to create chat metadata');
                }
              }
            } else {
              console.log('[chat.$projectId.$id] no cached data found, navigating to home');
              navigate(`/`, { replace: true });
            }
          } catch (error) {
            toast.error((error as Error).message || 'Failed to load chat data');
          }
        } else {
          // fetching data from cloud
          if (!mixedCloudChatId || !currentVersion) {
            setReady(true);
            throw new Error('Mixed cloud chat ID or current version not found');
          }

          console.log('[useChatHistory] cloudserver response:', loaderFileActions, loaderMessages);

          projectId.set(mixedProjectId);
          description.set(projectName ?? undefined);
          cloudChatId.set(mixedCloudChatId);
          versionId.set(currentVersion);

          // Cache to IDB
          try {
            const messages = (loaderMessages ?? []).map((msg) => ({
              ...msg,
              createdAt: msg.createdAt ? new Date(msg.createdAt) : undefined,
            })) as UIMessage[];
            const fileActions = (loaderFileActions ?? []) as StoreFileAction[];

            await setMessages(
              db,
              mixedProjectId,
              messages,
              projectData?.techStack?.framework ?? '',
              projectData?.techStack?.language ?? '',
              projectData?.techStack?.packageManager ?? '',
              projectData?.isGitRepo ?? false,
              projectData?.initSource ?? undefined,
              projectData?.projectName ?? undefined,
              projectData?.connections ?? undefined,
              token ?? undefined,
              mixedCloudChatId,
              projectData?.cacheTime ?? undefined,
              undefined,
              undefined,
              true,
            );
            await setFileActions(db, mixedProjectId, mixedCloudChatId, fileActions, true);

            if (projectData?.netlifyCreds) {
              updateNetlifyUrl(db, mixedProjectId, projectData.netlifyCreds);
            }

            setInitialMessages(messages);
            setInitialFileActions(fileActions);
            console.log('[useChatHistory] fileActions set in IDB');
          } catch (error) {
            console.warn('[hit] IDB cache write failed:', error);
          }
        }

        // Delete completed events in the background
        if (eventsToDelete && eventsToDelete.length > 0) {
          console.log('[useChatHistory] Deleting completed events');
          deleteEvents(db, eventsToDelete).catch((error) => {
            console.warn('[DualiteEvents] Failed to delete completed events:', error);
          });
        }

        setReady(true);
      } catch (error) {
        toast.error((error as Error).message || 'Failed to load chat data');
        throw new Error('Failed to load chat data');

        // navigate('/', { replace: true });
      }
    };

    loadChatData();
  }, [navigation.state, mixedProjectId, mixedCloudChatId, currentVersion, projectName, cacheHit]);

  const handleCloseErrorModal = () => {
    setShowCloudProjectErrorModal(false);
    window.location.href = '/';
  };

  return {
    ready: !mixedProjectId || ready,
    initialMessages,
    initialFileActions,
    showCloudProjectErrorModal,
    errorMessage,
    handleCloseErrorModal,
    storeMessageHistory: async (
      messages: UIMessage[],
      framework: string,
      language: string,
      packageManager: string,
      token: string,
      isGitRepo: boolean,
      initSource: string,
      connections?: Connections,
    ) => {
      console.log('[supa] storeMessageHistory called from useChatHistory: ', connections);

      if (!db || messages.length === 0) {
        return;
      }

      await getData();

      console.log('[flow] storeMessageHistory called from useChatHistory, projectId');

      await setMessages(
        db as IDBDatabase,
        projectId.get() as string,
        messages,
        framework,
        language,
        packageManager,
        (isGitRepo = isGitRepo || false),
        initSource,
        description.get(),
        connections,
        token ?? undefined,
        cloudChatId.get() as string,
      );
      console.log('[refresh] storeMessageHistory setMessages called:');
    },
    storeFileActions: async (fileActions: StoreFileAction[]) => {
      if (!db || fileActions.length === 0) {
        return;
      }

      console.log('[flow] set file actions called from storeFileActions inside return function in usechathistory');

      await getData();
      await setFileActions(
        db as IDBDatabase,
        projectId.get() as string,
        cloudChatId.get() as string,
        fileActions,
        false,
      );
    },
    getFileActions: async () => {
      if (!db) {
        return [];
      }

      return await getFileActions(db as IDBDatabase, projectId.get() as string);
    },
  };
}

function navigateChat(id: string) {
  // url -> /chat/<projectId>
  const url = new URL(window.location.href);
  url.pathname = `/chat/${id}`;
  window.history.replaceState({}, '', url);
}

export async function storeFileActions(fileActions: StoreFileAction[], changeSource: 'manual' | 'ai' = 'ai') {
  console.log('[storeFileActions] fileActions length: ', fileActions.length, ' db: ', db);

  if (!db || fileActions.length === 0) {
    return;
  }

  console.log('[storeFileActions] fileActions: ', fileActions);

  await getData();

  console.log('[flow] set file actions called from storeFileActions');

  console.log('[DualiteEvents] chatId: ', cloudChatId.get());

  await setFileActions(
    db as IDBDatabase,
    projectId.get() as string,
    cloudChatId.get() as string,
    fileActions,
    false,
    changeSource,
  );
}

export function setDescriptionFromExamplePrompt(title: string) {
  description.set(title);
}

const updateCloudChatIdInIDB = async (cId: string) => {
  if (db) {
    await setCloudChatId(db as IDBDatabase, projectId.get() as string, cId);
  }
};

/*
 * update projectId in indexedDB
 * const updateProjectIdInIDB = async (pId: string) => {
 *   if (db) {
 *     await setProjectId(db as IDBDatabase, pId);
 *   }
 * };
 */

// after project is created, add github connection, if present
const updateGithubConnection = async () => {
  const repoName = githubRepoStore.get();
  const ownerName = githubOwnerStore.get();
  const branch = githubBranchStore.get();

  const isGitRepo = repoName && ownerName && branch;
  const pId = projectId.get();

  if (isGitRepo && pId) {
    const connectionData: ConnectionData = {
      connectionType: 'github',
      connectionData: {
        repoName,
        ownerName,
        branch,
      },
    };

    console.log('[projects] projectId from github connection: ', pId);

    await updateProjectConnection(pId, connectionData);
  }
};

const updateSupabaseConnection = async () => {
  const supabaseProjectId = supabaseProjectIdStore.get();
  const supabaseProjectName = supabaseProjectNameStore.get();
  const supabaseAnonKey = supabaseAnonKeyStore.get();
  const supabaseOrganizationId = supabaseOrganizationIdStore.get();
  const supabaseOrganizationName = supabaseOrganizationNameStore.get();

  const pId = projectId.get();

  if (
    pId &&
    supabaseProjectId &&
    supabaseProjectName &&
    supabaseAnonKey &&
    supabaseOrganizationId &&
    supabaseOrganizationName
  ) {
    const connectionData: ConnectionData = {
      connectionType: 'supabase',
      connectionData: {
        anonKey: supabaseAnonKey,
        organizationId: supabaseOrganizationId,
        organizationName: supabaseOrganizationName,
        projectId: supabaseProjectId,
        projectName: supabaseProjectName,
      },
    };

    await updateProjectConnection(pId, connectionData);
  }
};

const createCloudProject = async (
  framework: string,
  language: string,
  packageManager: string,
  initSource: string,
  isGitRepo: boolean,
  description: string,
) => {
  console.log('[flow] Conditions met for project upload, preparing data...');

  const [fwork, cssLib] = framework.split('_');
  const techStack = {
    framework: fwork,
    cssLib,
    language,
    packageManager,
  };
  const source = initSource == 'alpha' ? 'alpha' : 'github';

  const valuesArray = [
    {
      currentVersion: null,
      projectName: description || '',
      techStack,
      isGitRepo,
      initSource: source,
      type: 'private' as const, // all new projects are now created as cloud projects
      urlId: null,
      idbChatId: null,
      createdAt: new Date(),
    },
  ];

  try {
    const response = await fetch('/api/projects', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ projectData: valuesArray, type: 'create' }),
    });

    const result = (await response.json()) as {
      ok: boolean;
      message: { projectId: string; cloudChatId: string };
      error: string;
    };

    console.log(
      '[flow] projectId and cloudChatId from createCloudProject: ',
      result.message?.projectId,
      result.message?.cloudChatId,
    );

    if (result.ok) {
      const { projectId: pId, cloudChatId: cId } = result.message;
      projectId.set(pId);
      cloudChatId.set(cId);

      navigateChat(pId);

      updateGithubConnection();
      updateSupabaseConnection();

      // update the sidebar with the new project
      const newProject: CloudChatHistoryItem = {
        ...valuesArray[0],
        projectId: pId,
        cloudChatId: cId,
        currentVersion: null,
        timestamp: valuesArray[0].createdAt.toISOString(),
        projectRules: null,
        cacheTime: null,
      };

      await addProjectToStore(newProject);
      console.log('[updateProjectInStore] New project added to sidebar');
    }
  } catch (error) {
    console.error('[projects] Error creating cloud project:', error);
    Sentry.captureException(`Error from createCloudProject in useChatHistory.ts: ${error}`, {
      extra: {
        framework,
        language,
        packageManager,
        initSource,
      },
    });

    /*
     * setErrorMessage('Failed to create project. Please try again.');
     * setShowCloudProjectErrorModal(true);
     */
    toast.error('Failed to create project');
  }
};
